import { useEffect, useState } from "react";
import { Copy, FileEdit, History, Plus, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError, formatDate } from "@/lib/api";
import { CONTENT_STATUS_LABELS, CONTENT_TYPE_LABELS } from "@/lib/publicApi";
import { Loading, Pill, StateBlock } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Action, PageHeader, SelectField, Toolbar } from "./ui";
import { MarkdownEditor } from "@/components/MarkdownEditor";

const STATUS_TONES = {
  draft: "warning",
  in_review: "info",
  approved: "info",
  scheduled: "info",
  published: "success",
  archived: "neutral",
};

const TREATMENT_BLOCKS = [
  ["what_is", "O que é"],
  ["signs", "Sinais de atenção"],
  ["impacts", "Impactos possíveis"],
  ["when_to_seek", "Quando procurar avaliação"],
  ["how_assessment_works", "Como funciona a avaliação"],
  ["treatment_options", "Formas de tratamento"],
  ["faq", "Perguntas frequentes"],
];

const emptyForm = {
  type: "article",
  title: "",
  slug: "",
  summary: "",
  body: "",
  blocks: {},
  category: "",
  categories: [],
  tags: "",
  featured: false,
  order: 0,
  image_url: "",
  image_alt: "",
  related_slugs: "",
  author: "Equipe Erimus",
  reviewer: "",
  references: "",
  reading_minutes: 3,
  needs_clinical_review: true,
};

export default function ContentManager() {
  const { api, user } = useAuth();
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState(null);
  const [filter, setFilter] = useState({ type: "", status: "", category: "" });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [versions, setVersions] = useState([]);
  const [scheduleAt, setScheduleAt] = useState("");
  const [notifyByItem, setNotifyByItem] = useState({});
  const notifyFor = (id) => notifyByItem[id] !== false;

  const load = async () => {
    try {
      const [list, metaResp] = await Promise.all([
        api.get("/portal/content", {
          params: {
            type: filter.type || undefined,
            status: filter.status || undefined,
            category: filter.category || undefined,
          },
        }),
        api.get("/portal/content-meta"),
      ]);
      setItems(list.data);
      setMeta(metaResp.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.type, filter.status, filter.category]);

  const run = async (fn, message) => {
    try {
      const res = await fn();
      if (message) toast.success(message);
      await load();
      return res;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    }
  };

  const openEditor = async (item) => {
    if (!item) {
      setEditing("new");
      setForm(emptyForm);
      setVersions([]);
      return;
    }
    const { data } = await api.get(`/portal/content/${item.id}`);
    setEditing(item.id);
    setVersions(data.versions);
    setForm({
      ...emptyForm,
      ...data.content,
      tags: (data.content.tags || []).join(", "),
      related_slugs: (data.content.related_slugs || []).join(", "),
      blocks: data.content.blocks || {},
      category: data.content.category || "",
      categories: data.content.categories || (data.content.category ? [data.content.category] : []),
    });
  };

  const payload = () => ({
    type: form.type,
    title: form.title,
    slug: form.slug || undefined,
    summary: form.summary,
    body: form.body,
    blocks: form.blocks,
    category: (form.categories && form.categories[0]) || form.category || null,
    categories: form.categories || [],
    tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
    featured: form.featured,
    order: Number(form.order) || 0,
    image_url: form.image_url || null,
    image_alt: form.image_alt || null,
    related_slugs: form.related_slugs ? form.related_slugs.split(",").map((t) => t.trim()).filter(Boolean) : [],
    author: form.author || null,
    reviewer: form.reviewer || null,
    references: form.references || null,
    reading_minutes: Number(form.reading_minutes) || 3,
    needs_clinical_review: form.needs_clinical_review,
  });

  const save = async (e) => {
    e.preventDefault();
    const result =
      editing === "new"
        ? await run(() => api.post("/portal/content", payload()), "Conteúdo criado como rascunho")
        : await run(() => api.put(`/portal/content/${editing}`, payload()), "Conteúdo salvo (nova versão)");
    if (result) setEditing(null);
  };

  if (items === null) return <Loading label="Carregando conteúdo público…" />;

  const canEdit = ["content_manager", "clinical_coordinator", "admin"].includes(user?.role);
  const canPublish = ["clinical_coordinator", "admin"].includes(user?.role);

  return (
    <div data-testid="portal-content-page" className="space-y-6">
      <PageHeader
        kicker="Conteúdo público"
        title="Gestão editorial"
        subtitle="Fluxo: rascunho → em revisão → aprovado → publicado/agendado → arquivado. Somente publicado (ou agendado com data vencida) aparece no aplicativo."
        actions={
          canEdit ? (
            <Action icon={Plus} data-testid="content-new-button" onClick={() => openEditor(null)}>
              Novo conteúdo
            </Action>
          ) : null
        }
      />

      <Toolbar testId="content-toolbar">
        <SelectField
          testId="content-filter-type"
          value={filter.type}
          onChange={(e) => setFilter({ ...filter, type: e.target.value })}
        >
          <option value="">Todos os tipos</option>
          {(meta?.types || []).map((t) => (
            <option key={t} value={t}>
              {CONTENT_TYPE_LABELS[t] || t}
            </option>
          ))}
        </SelectField>
        <SelectField
          testId="content-filter-status"
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="">Todos os status</option>
          {(meta?.statuses || []).map((s) => (
            <option key={s} value={s}>
              {CONTENT_STATUS_LABELS[s] || s}
            </option>
          ))}
        </SelectField>
        <SelectField
          testId="content-filter-category"
          value={filter.category}
          onChange={(e) => setFilter({ ...filter, category: e.target.value })}
        >
          <option value="">Todos os temas</option>
          {(meta?.categories || []).map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.name}
            </option>
          ))}
        </SelectField>
      </Toolbar>

      {editing ? (
        <form onSubmit={save} data-testid="content-editor" className="er-card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-medium text-ink-900">
              {editing === "new" ? "Novo conteúdo" : "Editar conteúdo"}
            </h2>
            <Button type="button" variant="ghost" data-testid="content-editor-close" onClick={() => setEditing(null)} className="rounded-full">
              Fechar
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-sm text-ink-600">Tipo</Label>
              <select
                data-testid="content-type-select"
                value={form.type}
                disabled={editing !== "new"}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="mt-2 h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
              >
                {(meta?.types || []).map((t) => (
                  <option key={t} value={t}>{CONTENT_TYPE_LABELS[t] || t}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm text-ink-600">Temas (categorias)</Label>
              <div className="mt-2 rounded-xl border border-border bg-white p-3">
                <p className="mb-2 text-[11px] text-ink-400">
                  Salvar um conteúdo já publicado cria uma nova versão em rascunho: ele sai do ar até ser publicado de novo.
                </p>
                <div
                  data-testid="content-categories-field"
                  className="max-h-44 space-y-2 overflow-y-auto"
                >
                {(meta?.categories || []).length === 0 ? (
                  <p className="text-xs text-ink-400">Nenhuma categoria cadastrada.</p>
                ) : (
                  Object.entries(
                    (meta?.categories || []).reduce((acc, c) => {
                      const key = c.group_label || "Outros temas";
                      acc[key] = [...(acc[key] || []), c];
                      return acc;
                    }, {})
                  ).map(([group, items]) => (
                    <div key={group}>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">{group}</p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {items.map((c) => {
                          const active = (form.categories || []).includes(c.slug);
                          return (
                            <button
                              key={c.slug}
                              type="button"
                              data-testid={`content-category-${c.slug}`}
                              onClick={() =>
                                setForm({
                                  ...form,
                                  categories: active
                                    ? form.categories.filter((s) => s !== c.slug)
                                    : [...(form.categories || []), c.slug],
                                })
                              }
                              className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                                active ? "bg-sage-600 text-white" : "bg-sand text-ink-600 hover:bg-sage-50"
                              }`}
                            >
                              {c.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm text-ink-600">Título</Label>
            <Input data-testid="content-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 h-11 rounded-xl" required />
          </div>
          <div>
            <Label className="text-sm text-ink-600">Resumo</Label>
            <Textarea data-testid="content-summary-input" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={2} className="mt-2 rounded-xl" />
          </div>
          <div>
            <Label className="text-sm text-ink-600">Texto</Label>
            <div className="mt-2">
              <MarkdownEditor
                testId="content-body-editor"
                label="Texto do conteúdo"
                value={form.body}
                onChange={(value) => setForm({ ...form, body: value })}
              />
            </div>
          </div>

          {form.type === "treatment" ? (
            <div className="space-y-3 rounded-xl bg-sand/50 p-4">
              <p className="er-label">Blocos do tratamento</p>
              {TREATMENT_BLOCKS.map(([key, label]) => (
                <div key={key}>
                  <Label className="text-sm text-ink-600">{label}</Label>
                  <div className="mt-2">
                    <MarkdownEditor
                      testId={`content-block-${key}`}
                      label={label}
                      rows={4}
                      value={form.blocks?.[key] || ""}
                      onChange={(value) => setForm({ ...form, blocks: { ...form.blocks, [key]: value } })}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label className="text-sm text-ink-600">Tags (vírgula)</Label>
              <Input data-testid="content-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-2 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-sm text-ink-600">Ordem</Label>
              <Input data-testid="content-order-input" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-2 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-sm text-ink-600">Minutos de leitura</Label>
              <Input data-testid="content-minutes-input" type="number" value={form.reading_minutes} onChange={(e) => setForm({ ...form, reading_minutes: e.target.value })} className="mt-2 h-11 rounded-xl" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-sm text-ink-600">Imagem (URL)</Label>
              <Input data-testid="content-image-input" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-2 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-sm text-ink-600">Texto alternativo da imagem</Label>
              <Input data-testid="content-image-alt-input" value={form.image_alt || ""} onChange={(e) => setForm({ ...form, image_alt: e.target.value })} className="mt-2 h-11 rounded-xl" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-sm text-ink-600">Relacionados (slugs, vírgula)</Label>
              <Input data-testid="content-related-input" value={form.related_slugs} onChange={(e) => setForm({ ...form, related_slugs: e.target.value })} className="mt-2 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-sm text-ink-600">Referências editoriais</Label>
              <Input data-testid="content-references-input" value={form.references || ""} onChange={(e) => setForm({ ...form, references: e.target.value })} className="mt-2 h-11 rounded-xl" />
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-ink-600">
            <Checkbox data-testid="content-featured-checkbox" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: !!v })} />
            Destacar na tela inicial
          </label>
          <label className="flex items-center gap-3 text-sm text-ink-600">
            <Checkbox data-testid="content-review-checkbox" checked={form.needs_clinical_review} onCheckedChange={(v) => setForm({ ...form, needs_clinical_review: !!v })} />
            Exibir aviso de "precisa de revisão clínica"
          </label>

          <Button type="submit" data-testid="content-save-button" className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700">
            Salvar
          </Button>

          {versions.length ? (
            <div className="border-t border-border pt-4">
              <p className="er-label flex items-center gap-2"><History size={14} /> Versões anteriores</p>
              <div className="mt-3 space-y-2">
                {versions.map((v) => (
                  <div key={v.id} data-testid={`content-version-${v.version}`} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-ink-600">
                      v{v.version} · {v.reason} · {formatDate(v.created_at)}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid={`content-restore-${v.version}`}
                      onClick={() =>
                        run(async () => {
                          const res = await api.post(`/portal/content/${editing}/restore/${v.version}`);
                          await openEditor({ id: editing });
                          return res;
                        }, `Versão ${v.version} restaurada como nova versão`)
                      }
                      className="rounded-full"
                    >
                      Restaurar
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </form>
      ) : null}

      {items.length === 0 ? (
        <StateBlock testId="content-empty" icon={FileEdit} title="Nenhum conteúdo com esses filtros" />
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} data-testid={`content-row-${item.slug}`} className="er-card space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill>{CONTENT_TYPE_LABELS[item.type] || item.type}</Pill>
                    <Pill tone={STATUS_TONES[item.status]} testId={`content-status-${item.slug}`}>
                      {CONTENT_STATUS_LABELS[item.status] || item.status}
                    </Pill>
                    <span className="text-xs text-ink-400">v{item.version}</span>
                    {item.clinical_review_pending ? (
                      <Pill tone="warning" testId={`content-clinical-review-${item.slug}`}>
                        Pendente de revisão clínica
                      </Pill>
                    ) : null}
                  </div>
                  <h3 className="mt-2 font-display text-lg font-medium text-ink-900">{item.title}</h3>
                  <p className="text-xs text-ink-400">
                    /{item.slug} · atualizado em {formatDate(item.updated_at)}
                    {item.scheduled_for ? ` · agendado para ${formatDate(item.scheduled_for)}` : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" data-testid={`content-edit-${item.slug}`} onClick={() => openEditor(item)} className="rounded-full">
                    <FileEdit size={14} className="mr-1" /> Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`content-duplicate-${item.slug}`}
                    onClick={() => run(() => api.post(`/portal/content/${item.id}/duplicate`), "Conteúdo duplicado")}
                    className="rounded-full"
                  >
                    <Copy size={14} className="mr-1" /> Duplicar
                  </Button>
                  {(meta?.transitions?.[item.status] || [])
                    .filter((target) => canPublish || !["approved", "published", "scheduled"].includes(target))
                    .map((target) => (
                    <Button
                      key={target}
                      size="sm"
                      data-testid={`content-transition-${item.slug}-${target}`}
                      onClick={() => {
                        if (target === "scheduled") {
                          const when = scheduleAt || new Date(Date.now() + 3600000).toISOString();
                          run(() => api.post(`/portal/content/${item.id}/transition`, { status: target, scheduled_for: when }), "Publicação agendada");
                        } else {
                          run(
                            () => api.post(`/portal/content/${item.id}/transition`, { status: target, notify_users: notifyFor(item.id) }),
                            target === "published" && notifyFor(item.id)
                              ? "Conteúdo publicado e aviso enviado aos usuários"
                              : `Status alterado para ${CONTENT_STATUS_LABELS[target] || target}`
                          );
                        }
                      }}
                      className="rounded-full bg-sand text-ink-700 hover:bg-sage-600 hover:text-bone"
                    >
                      <Send size={13} className="mr-1" /> {CONTENT_STATUS_LABELS[target] || target}
                    </Button>
                  ))}
                </div>
              </div>
              {(meta?.transitions?.[item.status] || []).includes("published") && canPublish ? (
                <label className="flex items-center gap-3 text-xs text-ink-600">
                  <Checkbox
                    data-testid={`content-notify-${item.slug}`}
                    checked={notifyFor(item.id)}
                    onCheckedChange={(v) => setNotifyByItem((s) => ({ ...s, [item.id]: !!v }))}
                  />
                  Notificar usuários do app Saúde Mental ao publicar
                </label>
              ) : null}
              {item.status === "approved" ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Label className="text-xs text-ink-500">Agendar para</Label>
                  <Input
                    data-testid={`content-schedule-input-${item.slug}`}
                    type="datetime-local"
                    value={scheduleAt}
                    onChange={(e) => setScheduleAt(new Date(e.target.value).toISOString())}
                    className="h-10 w-56 rounded-xl"
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
